import { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UseTeamCreateFlowParams {
  maxTeamNameLength: number;
  isCreatingTeam: boolean;
  createTeamError: string | null;
  clearCreateTeamError: () => void;
  createTeam: (teamName: string) => Promise<string | null>;
  onTeamCreated: (teamId: string) => void;
}

export const useTeamCreateFlow = ({
  maxTeamNameLength,
  isCreatingTeam,
  createTeamError,
  clearCreateTeamError,
  createTeam,
  onTeamCreated,
}: UseTeamCreateFlowParams) => {
  const { t } = useTranslation();
  const [teamName, setTeamName] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isCreateTeamModalOpen, setIsCreateTeamModalOpen] = useState(false);

  const createError = useMemo(() => {
    return validationError || createTeamError;
  }, [validationError, createTeamError]);

  const isCreateDisabled = useMemo(() => {
    return (
      isCreatingTeam || teamName.trim().length === 0 || teamName.trim().length > maxTeamNameLength
    );
  }, [isCreatingTeam, teamName, maxTeamNameLength]);

  const resetCreateTeamState = () => {
    setTeamName('');
    setValidationError(null);
    clearCreateTeamError();
  };

  const openCreateTeamModal = () => {
    resetCreateTeamState();
    setIsCreateTeamModalOpen(true);
  };

  const closeCreateTeamModal = () => {
    if (isCreatingTeam) {
      return;
    }

    setIsCreateTeamModalOpen(false);
    resetCreateTeamState();
  };

  const handleTeamNameChange = (nextName: string) => {
    setTeamName(nextName);

    if (validationError) {
      setValidationError(null);
    }

    if (createTeamError) {
      clearCreateTeamError();
    }
  };

  const handleCreateTeam = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedTeamName = teamName.trim();

    if (!normalizedTeamName) {
      setValidationError(t('team.settings.rename.validation.nameRequired'));
      return;
    }

    if (normalizedTeamName.length > maxTeamNameLength) {
      setValidationError(
        t('team.settings.rename.validation.maxLength', { count: maxTeamNameLength })
      );
      return;
    }

    setValidationError(null);
    clearCreateTeamError();

    const createdTeamId = await createTeam(normalizedTeamName);
    if (!createdTeamId) {
      return;
    }

    setIsCreateTeamModalOpen(false);
    resetCreateTeamState();
    onTeamCreated(createdTeamId);
  };

  return {
    teamName,
    createError,
    isCreateDisabled,
    isCreateTeamModalOpen,
    handleTeamNameChange,
    handleCreateTeam,
    openCreateTeamModal,
    closeCreateTeamModal,
  };
};
